/**
 * ant-design-vue 1.x（rc-form）表单写法的 Vue3 兼容对象
 *
 * Vue2 时代写法：
 *   data() { return { form: this.$form.createForm(this) } }
 *   <a-form :form="form"> <a-input v-decorator="['name', { initialValue, rules }]" /> </a-form>
 *   this.form.validateFields((errors, values) => {})
 *
 * ant-design-vue 4 已删除 Form.create()/v-decorator，改为 <a-form :model> + a-form-item name/rules + v-model:value。
 * 模板已由 scripts/vue3_rcform.mjs 批量改写（v-decorator -> v-model:value、rules -> a-form-item :rules、a-form 加 ref/model）。
 * 本文件负责让原来的 this.form 方法调用（validateFields/resetFields/setFieldsValue/getFieldValue）继续可用，
 * 把数据本身放在 form 对象上，与模板里的 form.xxx 双向绑定天然一致。
 */

import { reactive } from 'vue'

// 找到组件里 antd v4 的 a-form 实例（模板改写时统一加了 ref="formRef"）
function resolveFormRef(vm) {
    if (!vm || !vm.$refs) return null
    const candidates = [vm.$refs.formRef, vm.$refs.form, ...Object.keys(vm.$refs).map(k => vm.$refs[k])]
    for (const ref of candidates) {
        const inst = Array.isArray(ref) ? ref[0] : ref
        if (inst && (typeof inst.validate === 'function' || typeof inst.validateFields === 'function')) {
            return inst
        }
    }
    return null
}

// 把 antd v4 validate() reject 的 { errorFields: [{ name, errors }], values }
// 归一化成 rc-form 的 { 字段名: { errors: [{ field, message }] } }，
// 老页面统一按 errors[field].errors[0].message 消费
function normalizeValidateErrors(err) {
    if (!err) return null
    if (Array.isArray(err.errorFields)) {
        const errors = {}
        err.errorFields.forEach(ef => {
            // ef.name 是校验路径数组（嵌套字段为 ['a','b']），顶层字段取首段
            const field = Array.isArray(ef.name) ? ef.name.join('.') : ef.name
            if (field == null) return
            if (!errors[field]) errors[field] = { errors: [] }
            ;(ef.errors || []).forEach(msg => {
                errors[field].errors.push({ field, message: msg })
            })
        })
        return Object.keys(errors).length ? errors : null
    }
    return err
}

// 深拷贝初始值（保留值为 undefined 的键），供 resetFields 还原嵌套表单
function deepClone(value) {
    if (Array.isArray(value)) return value.map(deepClone)
    if (value && typeof value === 'object') {
        const copy = {}
        Object.keys(value).forEach(key => {
            copy[key] = deepClone(value[key])
        })
        return copy
    }
    return value
}

// 需要绑定 this 的方法（页面里大量存在 `const { form: { setFieldsValue } } = this`
// 的解构调用，解构会丢失 this，方法必须自带绑定）
const BOUND_METHODS = ['getFieldValue', 'getFieldsValue', 'setFieldsValue', 'resetFields',
    'validateFields', 'validateFieldsAndScroll', 'isFieldsTouched']

export class FormCompat {
    /**
     * @param {object} vm 调用 createForm 的组件实例（rc-form 里的 this）
     * @param {object} initialValues 由 v-decorator 的 initialValue 汇总而来（构建期写入）
     */
    constructor(vm, initialValues = {}) {
        // 内部字段用 __ 前缀，避免和业务字段冲突；getFieldsValue 只返回 __fields 里登记的
        Object.defineProperty(this, '__vm', { value: vm, enumerable: false, writable: true, configurable: true })
        Object.defineProperty(this, '__fields', { value: Object.keys(initialValues), enumerable: false, writable: true, configurable: true })
        Object.defineProperty(this, '__initial', { value: deepClone(initialValues), enumerable: false, writable: true, configurable: true })
        Object.keys(initialValues).forEach(key => {
            this[key] = initialValues[key]
        })
        // 注意：这里不做 bind！方法绑定统一在 createForm 里绑到 reactive 代理上。
        // 原因见 createForm 注释：先 bind(raw) 再 bind(proxy) 时，JS 的 bind 链以
        // 第一次 bind 为准（内层优先），重绑到 proxy 是无效的，写入会绕过响应式代理。
    }

    // 兼容 rc-form 的字段读写
    getFieldValue(name) {
        return this[name]
    }

    getFieldsValue() {
        const values = {}
        const fields = this.__fields || []
        fields.forEach(key => {
            values[key] = this[key]
        })
        return values
    }

    setFieldsValue(values) {
        Object.keys(values || {}).forEach(key => {
            if (!this.__fields) {
                Object.defineProperty(this, '__fields', { value: [], enumerable: false, writable: true, configurable: true })
            }
            if (this.__fields.indexOf(key) === -1) this.__fields.push(key)
            this[key] = values[key]
        })
    }

    resetFields() {
        const initial = this.__initial || {}
        const fields = this.__fields || []
        fields.forEach(key => {
            this[key] = initial[key]
        })
    }

    /**
     * rc-form 风格的校验：cb(errors, values)，errors 为 null 表示通过
     * antd v4 的 validate() 失败时 reject 出 { errorFields: [...] }
     */
    validateFields(callback) {
        const vm = this.__vm
        const done = (errors) => {
            if (typeof callback === 'function') callback(errors, this.getFieldsValue())
        }
        const formRef = resolveFormRef(vm)
        if (formRef) {
            const validate = formRef.validate || formRef.validateFields
            const ret = validate.call(formRef)
            if (ret && typeof ret.then === 'function') {
                ret.then(() => done(null)).catch(err => {
                    // antd v4 Form 内部 handleSubmit 会先 emit('submit')（页面回调经本方法
                    // 先跑一轮 validateFields），随后自己又 validateFields 一次。两轮并发时，
                    // 先跑的那轮即使全部通过，resolve 后发现 lastValidatePromise 已被后者
                    // 顶掉，会走 Promise.reject([]) 分支，reject 出
                    // { values, errorFields: [], outOfDate }。errorFields 为空 = 该轮校验
                    // 实际全部通过，必须按通过处理，否则合法提交会拿到畸形 errors。
                    if (err && Array.isArray(err.errorFields) && err.errorFields.length === 0) {
                        done(null)
                        return
                    }
                    done(normalizeValidateErrors(err) || err || true)
                })
                return
            }
        }
        // 没有可用的 a-form 实例（弹窗尚未渲染等），退化为直接通过，避免阻塞流程
        done(null)
    }

    // 部分老代码用这两个名字
    validateFieldsAndScroll(callback) {
        this.validateFields(callback)
    }

    isFieldsTouched() {
        return true
    }
}

export function createForm(vm, initialValues) {
    const inst = new FormCompat(vm, initialValues)
    // 用响应式代理包裹：antd v4 FormItem 的 fieldValue 是 computed，依赖收集在
    // model 的 reactive 代理上；方法若绑在原始实例上，setFieldsValue 直接写 raw
    // 会绕过 proxy 的 set trap，computed 拿不到更新（校验值恒为旧值，表现为
    // "选了预约仍提示请选择预约项"）。这里构造器不 bind，创建代理后把方法
    // 一次性直接绑到响应式代理上（首次 bind 生效），保证写入走代理触发更新。
    const proxy = reactive(inst)
    BOUND_METHODS.forEach(name => {
        if (typeof inst[name] === 'function') inst[name] = inst[name].bind(proxy)
    })
    return proxy
}

export default { createForm }
